// =====================================================
// ActivityDashboard (Admin Activity Dashboard)
// =====================================================

import { useCallback, useEffect, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import Navbar from "./Navbar";
import { adminActivity } from "../services/activityApi";
import "./ActivityDashboard.css";

// ── formatting helpers ──
function fmtDuration(ms) {
  if (ms == null || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtRelative(v) {
  if (!v) return "—";
  const diff = Date.now() - new Date(v).getTime();
  if (diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const OVERVIEW_REFRESH_MS = 15 * 1000;

export default function ActivityDashboard() {
  const [tab, setTab] = useState("live"); // live | logins | pages

  // Overview cards + live online users (auto-refreshed).
  const [overview, setOverview] = useState(null);
  const [online, setOnline] = useState([]);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState("");

  // Timeline drill-down modal.
  const [timelineUser, setTimelineUser] = useState(null);

  // ── LIVE DATA (overview + online) ──
  const loadLive = useCallback(async (showSpinner) => {
    if (showSpinner) setLiveLoading(true);
    try {
      const [ov, on] = await Promise.all([
        adminActivity.overview(),
        adminActivity.online(),
      ]);
      setOverview(ov);
      setOnline(on);
      setLiveError("");
    } catch (err) {
      setLiveError(
        err.response?.data?.message || err.message || "Failed to load activity data"
      );
    } finally {
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLive(true);
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") loadLive(false);
    }, OVERVIEW_REFRESH_MS);
    return () => clearInterval(iv);
  }, [loadLive]);

  return (
    <div className="act-root">
      <Navbar />

      <div className="act-page">
        {/* ── HERO ── */}
        <div className="act-hero">
          <h1 className="act-title">📊 Activity Dashboard</h1>
          <p className="act-sub">
            Real-time &amp; historical user activity monitoring
          </p>
          <span className="act-live-dot">
            <i /> Live · refreshes every {OVERVIEW_REFRESH_MS / 1000}s
          </span>
        </div>

        {/* ── OVERVIEW CARDS ── */}
        <OverviewCards overview={overview} loading={liveLoading &&!overview} />

        {liveError && <div className="act-error">⚠️ {liveError}</div>}

        {/* ── TAB BAR ── */}
        <div className="act-tabs">
          {[
            { k: "live", label: `🟢 Online (${overview?.onlineUsers?? 0})` },
            { k: "logins", label: "🕑 Login History" },
            // { k: "visits", label: "🧭 Page Visits" }, // ✅ COMMENTED
            { k: "pages", label: "⏱️ Time per Page" },
          ].map((t) => (
            <button
              key={t.k}
              className={`act-tab ${tab === t.k? "act-tab-active" : ""}`}
              onClick={() => setTab(t.k)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB CONTENT ── */}
        {tab === "live" && (
          <OnlineUsers
            online={online}
            loading={liveLoading}
            onSelectUser={setTimelineUser}
          />
        )}
        {tab === "logins" && <LoginHistory onSelectUser={setTimelineUser} />}
        {/* {tab === "visits" && <PageVisitHistory />} */} {/* ✅ COMMENTED */}
        {tab === "pages" && <PageTimeStats />}
      </div>

      {timelineUser && (
        <TimelineModal
          user={timelineUser}
          onClose={() => setTimelineUser(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// OVERVIEW CARDS
// ─────────────────────────────────────────────────────
function OverviewCards({ overview, loading }) {
  const cards = [
    { icon: "👥", key: "totalUsers", label: "Total Users", cls: "" },
    { icon: "🟢", key: "onlineUsers", label: "Online Now", cls: "act-card-online" },
    { icon: "⚪", key: "offlineUsers", label: "Offline", cls: "" },
    { icon: "🔑", key: "todaysLogins", label: "Today's Logins", cls: "" },
    { icon: "⚡", key: "activeSessions", label: "Active Sessions", cls: "" },
  ];
  return (
    <div className="act-cards">
      {cards.map((c) => (
        <div key={c.key} className={`act-card ${c.cls}`}>
          <div className="act-card-icon">{c.icon}</div>
          <div className="act-card-body">
            <div className="act-card-num">
              {loading? "…" : overview? overview[c.key]?? 0 : 0}
            </div>
            <div className="act-card-label">{c.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────
// LIVE ONLINE USERS
// ─────────────────────────────────────────────────────
function OnlineUsers({ online, loading, onSelectUser }) {
  const [q, setQ] = useState("");

  const rows = online.filter(
    (u) =>
      (u.name || "").toLowerCase().includes(q.toLowerCase()) ||
      (u.email || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <section className="act-section">
      <div className="act-section-head">
        <h2>🟢 Currently Online</h2>
        <input
          className="act-search"
          placeholder="Search online users…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading && online.length === 0? (
        <TableSkeleton cols={6} />
      ) : rows.length === 0? (
        <EmptyState icon="😴" text="No users are online right now." />
      ) : (
        <div className="act-table-wrap">
          <table className="act-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Current Page</th>
                <th>Login Time</th>
                <th>Last Activity</th>
                <th>Active For</th>
                <th>Idle</th>
                <th>Device</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.sessionId} onClick={() => onSelectUser(u)}>
                  <td>
                    <div className="act-user">
                      <span className="act-online-dot" />
                      <div>
                        <div className="act-user-name">
                          {u.name}
                          {u.isAdmin && <span className="act-badge">ADMIN</span>}
                        </div>
                        <div className="act-user-mail">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="act-chip">{u.currentPage}</span>
                  </td>
                  <td>{fmtDateTime(u.loginTime)}</td>
                  <td>{fmtRelative(u.lastActivityAt)}</td>
                  <td>{fmtDuration(u.activeDurationMs)}</td>
                  <td>
                    {u.idleMs > 60000? (
                      <span className="act-idle">{fmtDuration(u.idleMs)}</span>
                    ) : (
                      <span className="act-active-txt">active</span>
                    )}
                  </td>
                  <td className="act-dim">
                    {u.device} · {u.browser}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────
// LOGIN HISTORY
// ─────────────────────────────────────────────────────
function LoginHistory({ onSelectUser }) {
  const [data, setData] = useState({ data: [], total: 0, totalPages: 0, page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("-loginTime");
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminActivity.logins({ search, page, limit: 15, sort });
      setData(res);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [search, page, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = (val) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(val);
    }, 350);
  };

  const toggleSort = (field) => {
    setSort((prev) => (prev === `-${field}`? field : `-${field}`));
    setPage(1);
  };

  return (
    <section className="act-section">
      <div className="act-section-head">
        <h2>🕑 Login History</h2>
        <input
          className="act-search"
          placeholder="Search by name or email…"
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>

      {error && <div className="act-error">⚠️ {error}</div>}

      {loading? (
        <TableSkeleton cols={7} />
      ) : data.data.length === 0? (
        <EmptyState icon="📭" text="No login records found." />
      ) : (
        <>
          <div className="act-table-wrap">
            <table className="act-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th className="act-sortable" onClick={() => toggleSort("loginTime")}>
                    Login {sortArrow(sort, "loginTime")}
                  </th>
                  <th>Logout</th>
                  <th className="act-sortable" onClick={() => toggleSort("durationMs")}>
                    Duration {sortArrow(sort, "durationMs")}
                  </th>
                  <th>Device</th>
                  <th>OS / Browser</th>
                  <th>IP</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => (
                  <tr
                    key={r.sessionId}
                    onClick={() => r.userId && onSelectUser({ userId: r.userId, name: r.name, email: r.email })}
                  >
                    <td>
                      <div className="act-user-name">{r.name}</div>
                      <div className="act-user-mail">{r.email}</div>
                    </td>
                    <td>{fmtDateTime(r.loginTime)}</td>
                    <td>{r.logoutTime? fmtDateTime(r.logoutTime) : "—"}</td>
                    <td>{fmtDuration(r.durationMs)}</td>
                    <td className="act-dim">{r.device}</td>
                    <td className="act-dim">
                      {r.os} · {r.browser}
                    </td>
                    <td className="act-dim act-mono">{r.ip || "—"}</td>
                    <td>
                      {r.online? (
                        <span className="act-status act-status-on">Online</span>
                      ) : (
                        <span className="act-status act-status-off">Offline</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}
    </section>
  );
}

/*
// ─────────────────────────────────────────────────────
// PAGE-VISIT HISTORY // ✅ COMPLETELY COMMENTED
// ─────────────────────────────────────────────────────
function PageVisitHistory() {
  const [data, setData] = useState({ data: [], total: 0, totalPages: 0, page: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminActivity.visits({ search, page, limit: 15 });
      setData(res);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = (val) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(val);
    }, 350);
  };

  return (
    <section className="act-section">
      <div className="act-section-head">
        <h2>🧭 Page Visit History</h2>
        <input
          className="act-search"
          placeholder="Search page or route…"
          onChange={(e) => onSearch(e.target.value)}
        />
      </div>

      {error && <div className="act-error">⚠️ {error}</div>}

      {loading? (
        <TableSkeleton cols={6} />
      ) : data.data.length === 0? (
        <EmptyState icon="🧭" text="No page visits recorded yet." />
      ) : (
        <>
          <div className="act-table-wrap">
            <table className="act-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Page</th>
                  <th>Route</th>
                  <th>Entry</th>
                  <th>Exit</th>
                  <th>Time Spent</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((v) => (
                  <tr key={v._id}>
                    <td>
                      <div className="act-user-name">{v.name}</div>
                      <div className="act-user-mail">{v.email}</div>
                    </td>
                    <td>
                      <span className="act-chip">{v.pageName}</span>
                    </td>
                    <td className="act-dim act-mono">{v.route}</td>
                    <td>{fmtDateTime(v.entryTime)}</td>
                    <td>
                      {v.ongoing? (
                        <span className="act-active-txt">on page</span>
                      ) : (
                        fmtDateTime(v.exitTime)
                      )}
                    </td>
                    <td>{fmtDuration(v.timeSpentMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={data.page}
            totalPages={data.totalPages}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}
    </section>
  );
}
*/

// ─────────────────────────────────────────────────────
// TIME PER PAGE
// ─────────────────────────────────────────────────────
function PageTimeStats() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await adminActivity.pageStats();
        setStats(res);
        setError("");
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const chartData = stats.slice(0, 10).map((s) => ({
    name: s.pageName,
    minutes: Math.round((s.totalMs / 60000) * 10) / 10,
  }));

  return (
    <section className="act-section">
      <div className="act-section-head">
        <h2>⏱️ Time Spent Per Page</h2>
      </div>

      {error && <div className="act-error">⚠️ {error}</div>}

      {loading? (
        <TableSkeleton cols={4} />
      ) : stats.length === 0? (
        <EmptyState icon="⏱️" text="No completed page visits to summarise yet." />
      ) : (
        <>
          <div className="act-chart">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#9ca3af", fontSize: 11 }}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={70}
                />
                <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} unit="m" />
                <Tooltip
                  contentStyle={{
                    background: "#111827",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12,
                    color: "#e5e7eb",
                  }}
                  formatter={(v) => [`${v} min`, "Total time"]}
                />
                <Bar dataKey="minutes" fill="#6332ff" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="act-table-wrap">
            <table className="act-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th>Route</th>
                  <th>Visits</th>
                  <th>Total Time</th>
                  <th>Avg / Visit</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((s) => (
                  <tr key={s.route}>
                    <td>
                      <span className="act-chip">{s.pageName}</span>
                    </td>
                    <td className="act-dim act-mono">{s.route}</td>
                    <td>{s.visits}</td>
                    <td>{fmtDuration(s.totalMs)}</td>
                    <td>{fmtDuration(s.avgMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────
// USER TIMELINE MODAL
// ─────────────────────────────────────────────────────
function TimelineModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await adminActivity.userTimeline(user.userId, { limit: 60 });
        setData(res);
        setError("");
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [user.userId]);

  return (
    <div className="act-modal-overlay" onClick={onClose}>
      <div className="act-modal" onClick={(e) => e.stopPropagation()}>
        <div className="act-modal-head">
          <div>
            <h3>{user.name}</h3>
            <p className="act-user-mail">{user.email}</p>
          </div>
          <button className="act-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {loading? (
          <div className="act-modal-body">
            <TableSkeleton cols={3} />
          </div>
        ) : error? (
          <div className="act-modal-body">
            <div className="act-error">⚠️ {error}</div>
          </div>
        ) : (
          <div className="act-modal-body">
            <div className="act-timeline-meta">
              <span>
                Status:{" "}
                {data.online? (
                  <span className="act-status act-status-on">Online</span>
                ) : (
                  <span className="act-status act-status-off">Offline</span>
                )}
              </span>
              <span>Last seen: {fmtRelative(data.lastSeen)}</span>
              <span>Last active: {fmtRelative(data.lastActive)}</span>
              {data.currentPage && (
                <span>
                  On page: <span className="act-chip">{data.currentPage}</span>
                </span>
              )}
            </div>

            {data.visits.length === 0? (
              <EmptyState icon="🧭" text="No page visits recorded for this user." />
            ) : (
              <ul className="act-timeline">
                {data.visits.map((v) => (
                  <li key={v._id} className="act-timeline-item">
                    <span className="act-timeline-dot" />
                    <div className="act-timeline-content">
                      <div className="act-timeline-top">
                        <span className="act-chip">{v.pageName}</span>
                        <span className="act-timeline-time">
                          {fmtDuration(v.timeSpentMs)}
                          {v.ongoing && " · ongoing"}
                        </span>
                      </div>
                      <div className="act-dim act-mono">{v.route}</div>
                      <div className="act-timeline-when">
                        {fmtDateTime(v.entryTime)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// SHARED UI BITS
// ─────────────────────────────────────────────────────
function Pager({ page, totalPages, total, onChange }) {
  if (totalPages <= 1) {
    return <div className="act-pager-info">{total} record(s)</div>;
  }
  return (
    <div className="act-pager">
      <button
        className="act-page-btn"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
      >
        ← Prev
      </button>
      <span className="act-pager-info">
        Page {page} / {totalPages} · {total} record(s)
      </span>
      <button
        className="act-page-btn"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
      >
        Next →
      </button>
    </div>
  );
}

function TableSkeleton({ cols = 5 }) {
  return (
    <div className="act-skeleton">
      {[...Array(6)].map((_, r) => (
        <div key={r} className="act-skeleton-row">
          {[...Array(cols)].map((__, c) => (
            <div key={c} className="act-skeleton-cell" />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="act-empty">
      <div className="act-empty-icon">{icon}</div>
      <p>{text}</p>
    </div>
  );
}

function sortArrow(sort, field) {
  if (sort === `-${field}`) return "↓";
  if (sort === field) return "↑";
  return "";
}